import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  getUserInvoiceSignatureApi,
  upsertUserInvoiceSignatureApi,
} from 'api/accountingInternalApi';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isSignaturePresent } from '../calc/signatureStorage';

const CANVAS_WIDTH = 420;
const CANVAS_HEIGHT = 140;

function getCanvasPoint(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function drawImageOnCanvas(canvas, dataUrl) {
  if (!canvas || !dataUrl) {
    return;
  }
  const context = canvas.getContext('2d');
  const image = new Image();
  image.onload = () => {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  };
  image.src = dataUrl;
}

function buildSignaturePayload({ dataUrl, signerName }) {
  return {
    dataUrl: dataUrl || '',
    signerName: signerName || '',
    signedAt: dataUrl ? new Date().toISOString() : null,
  };
}

const InvoiceSignaturePanel = ({
  userId,
  signerName,
  signature,
  onSignatureChange,
}) => {
  const toast = useToast();
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const userClearedRef = useRef(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);

  const emitSignature = useCallback(
    (dataUrl) => {
      onSignatureChange(buildSignaturePayload({ dataUrl, signerName }));
    },
    [onSignatureChange, signerName]
  );

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1a2b6d';
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const applyStoredSignature = useCallback(
    (stored) => {
      if (!stored?.dataUrl) {
        return false;
      }
      drawImageOnCanvas(canvasRef.current, stored.dataUrl);
      hasInkRef.current = true;
      onSignatureChange(buildSignaturePayload({ dataUrl: stored.dataUrl, signerName }));
      return true;
    },
    [onSignatureChange, signerName]
  );

  const fetchStoredSignature = useCallback(async () => {
    try {
      return await getUserInvoiceSignatureApi();
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    setupCanvas();
    if (signature?.dataUrl) {
      drawImageOnCanvas(canvasRef.current, signature.dataUrl);
      hasInkRef.current = true;
    }
  }, [setupCanvas, signature?.dataUrl]);

  useEffect(() => {
    if (userClearedRef.current || isSignaturePresent(signature) || !userId) {
      return;
    }
    let cancelled = false;
    const loadDefaultSignature = async () => {
      const stored = await fetchStoredSignature();
      if (cancelled || userClearedRef.current || !stored?.dataUrl) {
        return;
      }
      applyStoredSignature(stored);
    };
    loadDefaultSignature();
    return () => {
      cancelled = true;
    };
  }, [applyStoredSignature, fetchStoredSignature, signature, userId]);

  const startDrawing = (point) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    drawingRef.current = true;
    const context = canvas.getContext('2d');
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const draw = (point) => {
    if (!drawingRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.lineTo(point.x, point.y);
    context.stroke();
    hasInkRef.current = true;
  };

  const finishDrawing = () => {
    if (!drawingRef.current) {
      return;
    }
    drawingRef.current = false;
    if (hasInkRef.current) {
      emitSignature(canvasRef.current.toDataURL('image/png'));
    }
  };

  const handleClear = () => {
    userClearedRef.current = true;
    setupCanvas();
    hasInkRef.current = false;
    emitSignature('');
  };

  const handleSaveDefault = async () => {
    if (!isSignaturePresent(signature)) {
      return;
    }
    setSavingDefault(true);
    try {
      await upsertUserInvoiceSignatureApi({
        dataUrl: signature.dataUrl,
        signerName,
      });
      toast({
        title: 'Default signature saved',
        status: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save default signature',
        status: 'error',
      });
    } finally {
      setSavingDefault(false);
    }
  };

  const handleLoadDefault = async () => {
    setLoadingDefault(true);
    try {
      const stored = await fetchStoredSignature();
      if (!stored?.dataUrl) {
        toast({
          title: 'No saved signature',
          description: 'Save a default signature first.',
          status: 'info',
        });
        return;
      }
      userClearedRef.current = false;
      applyStoredSignature(stored);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load saved signature',
        status: 'error',
      });
    } finally {
      setLoadingDefault(false);
    }
  };

  return (
    <Box border="1px solid" borderColor="secondaryGray.200" borderRadius="md" p={4}>
      <Text fontWeight={700} mb={1}>
        Digital signature
      </Text>
      <Text fontSize="sm" color="secondaryGray.600" mb={4}>
        Sign below before saving the invoice. Your signature will appear on the invoice PDF.
      </Text>

      <FormControl mb={4}>
        <FormLabel>Signer name</FormLabel>
        <Text fontWeight={700}>{signerName || '—'}</Text>
      </FormControl>

      <Box
        border="1px dashed"
        borderColor="secondaryGray.300"
        borderRadius="md"
        bg="white"
        overflow="hidden"
        mb={3}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{ width: '100%', height: `${CANVAS_HEIGHT}px`, touchAction: 'none' }}
          onMouseDown={(event) => startDrawing(getCanvasPoint(canvasRef.current, event.clientX, event.clientY))}
          onMouseMove={(event) => draw(getCanvasPoint(canvasRef.current, event.clientX, event.clientY))}
          onMouseUp={finishDrawing}
          onMouseLeave={finishDrawing}
          onTouchStart={(event) => {
            event.preventDefault();
            const touch = event.touches[0];
            startDrawing(getCanvasPoint(canvasRef.current, touch.clientX, touch.clientY));
          }}
          onTouchMove={(event) => {
            event.preventDefault();
            const touch = event.touches[0];
            draw(getCanvasPoint(canvasRef.current, touch.clientX, touch.clientY));
          }}
          onTouchEnd={(event) => {
            event.preventDefault();
            finishDrawing();
          }}
        />
      </Box>

      <Flex gap={2} wrap="wrap">
        <Button size="sm" variant="outline" onClick={handleClear}>
          Clear
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleLoadDefault}
          isLoading={loadingDefault}
        >
          Load saved signature
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleSaveDefault}
          isLoading={savingDefault}
          isDisabled={!isSignaturePresent(signature)}
        >
          Save as my default
        </Button>
      </Flex>
    </Box>
  );
};

InvoiceSignaturePanel.propTypes = {
  userId: PropTypes.string,
  signerName: PropTypes.string.isRequired,
  signature: PropTypes.shape({
    dataUrl: PropTypes.string,
    signerName: PropTypes.string,
    signedAt: PropTypes.string,
  }),
  onSignatureChange: PropTypes.func.isRequired,
};

export default InvoiceSignaturePanel;
